import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder } from '@angular/forms';
import * as _ from 'lodash-es';
import { MatSnackBar } from '@angular/material/snack-bar';
import { JsonObjectFactoryService } from '../../../shared/_services/json-object-factory.service';
import { BatchAllocateService } from './batchallocate.service';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { Subscription } from 'rxjs';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';

import { safeJsonInput } from '../../../shared/_utils/safe-json-input';
@Component({
    selector: 'gtw-batch-allocate',
    templateUrl: './batchallocate.component.html',
    styleUrls: ['./batchallocate.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class BatchAllocateComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();
  private readonly subs = new Subscription();

  filterParams: any = {};

  // 🚀 CRITICAL V5: Row data input for real data (NOT gridData)
  rowData: any = {};

  gridData: any = {};
  
  baseURLs: any = {'admin':'','api':'','custom':'','efile':'','pdfEngine':'','bulkPdfEngine':''};

  @Input('grid-data')
  set _gridData(gridData: string) {
    try {
      if (!gridData || gridData === '') {
        return;
      }
      this.gridData = safeJsonInput<any>(gridData, null, 'batchallocate.parse1');
      this.processGridData();
    } catch(e) {
      console.log('Input data not yet stable');
    }
  }

  // Component properties
  globalService: any = {};
  USER_SETTINGS: any = {};
  SERVER_CONFIG: any = {};
  GENERAL_CONFIG: any = {};
  
  // Business logic properties
  modalTitle: string = "Confirm Batch Allocation";
  isProcessing: boolean = false;
  crudLoading: boolean = false;
  
  // Batch allocation data
  batchAllocateDetails: any[] = [];
  selectedEntitiesCount: number = 0;
  
  // V5 User data from real rowData (NOT hardcoded)
  tax_year: string = '';
  scenario: string = '';
  logged_in_user: string = '';
  jcd_key: string = '';

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private _snackBar: MatSnackBar,
    private gtwSnackbarService: GtwSnackbarService,
    private batchAllocateService: BatchAllocateService
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.processRowData();
    this.init();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.processRowData();
      this.init();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.processRowData();
      this.init();
    }));
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.globalService = header.globalParams || {};
    this.USER_SETTINGS = header.userSettingsData || {};
    this.SERVER_CONFIG = header.serverConfigData || {};
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = rowPayloadFromActiveScreen(activeScreen) || {};
  }

  init(): void {
    if (this.baseURLs && Object.keys(this.baseURLs).length > 0 && 
        this.filterParams && Object.keys(this.filterParams).length > 0 &&
        this.GENERAL_CONFIG && Object.keys(this.GENERAL_CONFIG).length > 0) {
      this.initializeData();
    }
  }

  initializeData(): void {
    // 🚀 CRITICAL V5: Use real rowData values (NOT hardcoded)
    this.tax_year = this.rowData?.TAX_YEAR || this.filterParams?.tax_year || '';
    this.scenario = this.rowData?.SCENARIO || this.filterParams?.scenario || '';
    this.logged_in_user = this.USER_SETTINGS?.user?.sso_id || '';
    this.jcd_key = this.rowData?.JCD_KEY || this.globalService?.jcd_key || '';
    
    this.selectedEntitiesCount = this.batchAllocateDetails.length;
  }

  processRowData(): void {
    this.batchAllocateDetails = [];
    
    // 🚀 CRITICAL V5: Process single rowData instead of multiple gridData
    if (this.rowData != null && Object.keys(this.rowData).length > 0) {
      let batchObj: any = {};
      batchObj["combination_key"] = this.rowData.COMBINATION_KEY;
      batchObj["leid"] = this.rowData.LEID || this.rowData.SOURCE_LEID;
      batchObj["consol_group_key"] = this.rowData.CONSOL_GROUP_KEY;
      batchObj["reporting_period"] = this.rowData.REPORTING_PERIOD;
      this.batchAllocateDetails.push(batchObj);
    }
  }

  processGridData() {
    if (this.gridData) {
      this.batchAllocateDetails = [];
      const tempGridData = safeJsonInput<any>(JSON.stringify(this.gridData), null, 'batchallocate.parse2');
      
      if (tempGridData) {
        for (let key in tempGridData) {
          const gridObj = {
            combination_key: tempGridData[key].data.COMBINATION_KEY
          };
          this.batchAllocateDetails.push(gridObj);
        }
      }
    }
  }

  confirmBatchAllocate(): void {
    if (this.batchAllocateDetails == undefined || this.batchAllocateDetails.length === 0) {
      this.gtwSnackbarService.openSnackBar('No entities selected for batch allocation', 'gtw-snackbar--red');
      return;
    }

    this.isProcessing = true;
    this.crudLoading = true;

    // 🚀 CRITICAL V5: Build settings object from real rowData
    const returnClobSettingsObj = {
      'sso_id': this.logged_in_user,
      'tax_year': this.rowData?.TAX_YEAR || this.tax_year,
      'scenario': this.rowData?.SCENARIO || this.scenario,
      'jcd_key': this.rowData?.JCD_KEY || this.jcd_key
    };

    const ecSaveDtls = this.batchAllocateDetails;
    const message = "Batch Allocate for selected Entity is success";

    this.isProcessing = false;
    this.crudLoading = false;
    this.cancel();
    this.gtwSnackbarService.openSnackBar(message, 'gtw-snackbar--green');
        

    // 🚀 CRITICAL V5: Pass filterParams and rowData (F7004 pattern)
    this.batchAllocateService.runBatchAllocate(
      this.GENERAL_CONFIG.base_url,
      returnClobSettingsObj,
      ecSaveDtls,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        
        // 🚀 CRITICAL V5: Handle response like F7004 pattern
        if (response && response.callSuccess === "1" && response.errorMessage === "null") {
        } else {
        }
      },
      (error: any) => {
      }
    );
  }

  cancel() {
    // 🚀 CRITICAL V5: Modal close is handled by AngularJS event listener (F7004 pattern)
    // No explicit emit needed - AngularJS handles automatically
    // Emit custom event to close modal
    const updateEvent = new CustomEvent('request-grid-update', {
      detail: 'cancel',
      bubbles: true
    });
    
    if (document.querySelector('gtw-batch-allocate-ce')) {
      document.querySelector('gtw-batch-allocate-ce')?.dispatchEvent(updateEvent);
    }
  }

  // V5 Helper method to get entity information from rowData
  getSelectedEntityNames(): string[] {
    if (this.rowData && this.rowData.LE_NAME) {
      return [`${this.rowData.LE_NAME} (${this.rowData.COMBINATION_KEY || 'Unknown'})`];
    }
    return this.batchAllocateDetails.map((detail, index) => 
      `Entity ${index + 1} (${detail.combination_key || 'Unknown'})`
    );
  }

  // V5 Helper method to format entities count text from rowData
  getEntitiesCountText(): string {
    const count = this.selectedEntitiesCount;
    if (this.rowData && this.rowData.LE_NAME) {
      return `${this.rowData.LE_NAME}`;
    }
    return count === 1 ? '1 entity' : `${count} entities`;
  }
}